export const paths = {
  root: '/',
  organizationalStructure: {
    root: '/organizational-structure',
    details: (id: string) => `/organizational-structure/details/${id}`
  },
  institutions: {
    root: '/institutions',
    details: (id?: string) => `/institutions/details/${id}`,
    edit: (id: string) => `/institutions/edit/${id}`,
    add: '/institutions/add',
    contratAdd: (id?: string) => `/institutions/details/${id}/contract`,
    contractEdit: (id?: string, contractId?: string) =>
      `/institutions/details/${id}/edit/${contractId}/contract`,
    templateContractAdd: (id?: string) =>
      `/institutions/details/${id}/template-contract`,
    templateContractEdit: (id?: string, contractId?: string) =>
      `/institutions/details/${id}/edit/${contractId}/template-contract`
  },
  users: {
    root: '/users',
    create: '/users/create',
    edit: (id: string) => `/users/edit/${id}`
  },
  patients: {
    root: '/patients',
    details: (id?: string) => `/patients/details/${id}/`,
    consultations: {
      root: () => `consultations`,
      new: (personId: string) =>
        `/patients/details/${personId}/consultations/new`,
      draft: (personId: string, draftId: string) =>
        `/patients/details/${personId}/consultations/draft/${draftId}`,
      details: (personId: string, id: string) =>
        `/patients/details/${personId}/consultations/${id}`,
      edit: (personId: string, id: string) =>
        `/patients/details/${personId}/consultations/${id}/edit`
    },
    treatments: {
      root: () => `treatments`,
      details: (personId: string, id: string) =>
        `/patients/details/${personId}/treatments/${id}`
    },
    investigations: {
      root: () => `investigations`,
      details: (personId: string, id: string) =>
        `/patients/details/${personId}/investigations/${id}`
    },
    investigationResults: {
      root: () => `investigation-results`
    },
    epidemiologyCases: {
      root: () => `epidemiology-cases`,
      add: (personId: string, consultationId?: string) =>
        consultationId
          ? `/patients/details/${personId}/epidemiology-cases/${consultationId}`
          : `/patients/details/${personId}/epidemiology-cases/new`,
      details: (personId: string, id: string) =>
        `/patients/details/${personId}/epidemiology-case/${id}`
    },
    recipes: {
      root: () => `recipes`,
      details: (personId: string, treatmentPlanId: string, id: string) =>
        `/patients/details/${personId}/treatments/${treatmentPlanId}/recipes/${id}`
    },
    pregnancy: {
      root: () => `pregnancy`,
      details: (personId: string, id: string) =>
        `/patients/details/${personId}/pregnancy/${id}`
    },
    quiz: {
      root: () => `quiz`,
      details: (personId: string, id: string) =>
        `/patients/details/${personId}/quiz/${id}`,
      complete: (id: string) => `/quiz/${id}/complete`,
      quizDetails: (id: string) => `/quiz/${id}/details`
    },
    transfers: {
      root: () => `transfer-history`
    },
    diagnoses: {
      root: () => `diagnoses`
    },
    simeTB: {
      root: (personId?: string, simeTBPersonId?: string) =>
        `/patients/details/${personId}/sime-tb/${simeTBPersonId}/treatments`,
      details: (personId?: string, simeTBPersonId?: string, id?: string) =>
        `/patients/details/${personId}/sime-tb/${simeTBPersonId}/treatment/${id}/details`
    },
    history: {
      root: () => `history`
    },
    riskGroups: {
      root: () => 'risk-groups'
    },
    sidaNotification: {
      add: (personId: string) =>
        `/patients/details/${personId}/sida-notification/add`,
      details: (personId: string) =>
        `/patients/details/${personId}/sida-notification/details`
    },
    hivAnamnesis: {
      root: (personId: string) => `/patients/details/${personId}/hiv-anamnesis`
    }
  },
  persons: {
    root: '/persons',
    details: (id: string) => `/persons/details/${id}/`,
    consultations: {
      root: () => `consultations`,
      new: (personId: string) =>
        `/persons/details/${personId}/consultations/new`,
      draft: (personId: string, draftId: string) =>
        `/persons/details/${personId}/consultations/draft/${draftId}`,
      details: (personId: string, id: string) =>
        `/persons/details/${personId}/consultations/${id}`,
      edit: (personId: string, id: string) =>
        `/persons/details/${personId}/consultations/${id}/edit`
    },
    investigations: {
      root: () => `investigations`,
      details: (personId: string, id: string) =>
        `/persons/details/${personId}/investigations/${id}`
    },
    investigationResults: {
      root: () => `investigation-results`
    },
    pregnancy: {
      root: () => `pregnancy`,
      details: (personId: string, id: string) =>
        `/persons/details/${personId}/pregnancy/${id}`
    },
    history: {
      root: () => `history`
    },
    riskGroups: {
      root: () => 'risk-groups'
    },
    screening: {
      root: () => `screening`
    }
  },
  stock: {
    root: '/stock',
    stock: '/stock/stock',
    applications: '/stock/applications',
    applicationDetails: (id: string) => `/stock/applications/details/${id}`,
    applicationCreate: `/stock/applications/create`,
    applicationEdit: (id?: string) => `/stock/applications/create/${id}`,
    issuanceDetails: (id: string) => `/stock/confirmed/issuance/details/${id}`,
    returns: {
      root: '/stock/returns',
      create: '/stock/returns/create',
      details: (id: string) => `/stock/returns/details/${id}`
    },
    stockGroups: '/stock/stock-groups',
    stockGroupsMedicine: '/stock/medicines',
    stockGroupsPharmaceutics: '/stock/pharmaceutics'
  },
  stockAdmin: {
    issuanceCreate: '/stock/admin/issuance/create',
    issuanceDetails: (id: string) => `/stock/admin/issuance/details/${id}`
  },
  stockLocal: {
    root: '/stock',
    stock: '/stock/local-stock',
    applications: '/stock/local-applications',
    applicationDetails: (id: string) =>
      `/stock/local-applications/details/${id}`,
    applicationCreate: `/stock/local-applications/create`,
    applicationEdit: (id?: string) => `/stock/local-applications/create/${id}`,
    issuanceDetails: (id: string) =>
      `/stock/confirmed/local-issuance/details/${id}`,
    stockAdmin: {
      issuanceDetails: (id: string) =>
        `/stock/admin/local-issuance/details/${id}`
    },
    returns: {
      root: '/stock/local-returns',
      create: '/stock/local-returns/create',
      details: (id: string) => `/stock/local-returns/details/${id}`
    },
    issuance: {
      root: '/stock/local-issuance',
      issuanceDetails: (id: string) => `/stock/local-issuance/details/${id}`
    },
    inputs: {
      root: '/stock/local-inputs',
      details: (id: string) => `/stock/local-inputs/details/${id}`,
      create: '/stock/local-inputs/create'
    }
  },
  issuance: {
    root: '/stock/issuance',
    issuanceDetails: (id: string) => `/stock/issuance/details/${id}`
  },
  inputs: {
    root: '/stock/inputs',
    details: (id: string) => `/stock/inputs/details/${id}`,
    create: '/stock/inputs/create'
  },
  settlements: {
    root: '/stock/settlements',
    details: (id: string) => `/stock/settlements/details/${id}`,
    create: '/stock/settlements/create',
    edit: (id: string) => `/stock/settlements/create/${id}`
  },
  reports: {
    root: '/reports',
    details: (id: string) => `/reports/details/${id}`
  },
  notificationTemplates: {
    root: '/notification-templates',
    create: '/notification-templates/create',
    edit: (id: string) => `/notification-templates/edit/${id}`
  },
  roles: '/roles',
  logs: '/logs',
  notifications: '/notifications',
  settings: '/settings',
  systemSettings: '/system-settings',
  auth: {
    root: '/auth',
    signup: '/auth/signup',
    confirmEmail: '/auth/confirm-email',
    changeEmail: '/auth/change-email',
    confirm: '/auth/confirm',
    forgotPassword: '/auth/forgot-password',
    resetPassword: 'reset-password',
    resetConfirm: '/auth/reset-confirm'
  },
  classifiers: {
    root: '/classifiers',
    details: (id: string) => `/classifiers/details/${id}`,
    opportunisticDiseases: '/classifiers/opportunistic-diseases'
  },
  appointments: '/appointments',
  screeningTb: '/screening-tb',
  personsFromScreening: '/persons-from-screening',
  treatments: {
    root: '/treatments',
    details: (id: string) => `/treatments/details/${id}`
  },
  personTestResults: '/person-tests-results',
  pregnancyMonitoring: '/pregnancy-monitoring',
  testsResults: '/test-results',
  investigations: '/investigations',
  analysis: {
    root: '/analysis',
    details: (id: string) => `/analysis/details/${id}`,
    addTest: (id: string) => `/analysis/details/${id}/add-test`
  },
  epidemiologyCases: {
    root: '/epidemiology-cases',
    view: (id: string) => `/epidemiology-cases/${id}`
  },
  visits: '/visits',
  transfers: '/transfers',
  liberations: '/liberations',
  reportsAdministration: {
    root: '/reports-administration',
    view: (id: string) => `/reports-administration/${id}`
  },
  supportInvestigation: '/support-investigation',
  counterpart: {
    root: '/counterparts',
    details: (id?: string) => `/counterparts/details/${id}`,
    edit: (id: string) => `/counterparts/edit/${id}`,
    add: '/counterparts/add',
    contratAdd: (id?: string) => `/counterparts/details/${id}/contract`,
    contractEdit: (id?: string, contractId?: string) =>
      `/counterparts/details/${id}/edit/${contractId}/contract`,
    templateContractAdd: (id?: string) =>
      `/counterparts/details/${id}/template-contract`,
    templateContractEdit: (id?: string, contractId?: string) =>
      `/counterparts/details/${id}/edit/${contractId}/template-contract`
  },
  medicinesRegister: {
    root: '/medicines-register',
    products: {
      root: '/medicines-register/products',
      add: '/medicines-register/products/create',
      edit: (id: string) => `/medicines-register/products/edit/${id}`
    },
    internationalNames: '/medicines-register/international-names',
    pharmaceuticalForms: '/medicines-register/pharmaceutical-forms',
    producers: '/medicines-register/producers',
    units: '/medicines-register/units',
    consumables: {
      root: '/medicines-register/consumables',
      add: '/medicines-register/consumables/create',
      edit: (id: string) => `/medicines-register/consumables/edit/${id}`
    }
  },
  riskGroups: {
    root: '/risk-groups',
    details: (id: string) => `/risk-groups/details/${id}`,
    personDetails: (riskId: string, id: string) =>
      `/risk-groups/person-details/${riskId}/${id}`,
    classifier: '/classifier/risk-groups'
  },
  tests: {
    root: '/tests',
    add: '/tests/add',
    details: (id: string) => `/tests/details/${id}`,
    specimenDetails: (id: string) => `/tests/specimen-details/${id}`,
    testDetails: (investigationId: string, id: string) =>
      `/tests/details/${investigationId}/test/${id}`,
    templates: '/investigation-templates'
  },
  questionnaires: {
    root: '/questionnaires',
    create: '/questionnaires/create',
    edit: (id: string) => `/questionnaires/${id}/edit`
  },
  institutionConsultations: '/institution/consultations',
  institutionInvestigations: '/institution/investigations',
  institutionEpidemiologyCases: '/institution/epidemiology-cases',
  institutionTreatments: '/institution/treatments',
  institutionPregnancy: '/institution/pregnancy',
  institutionPrescriptions: '/institution/prescriptions',
  institutionBirths: '/institution/births',
  institutionNewborns: '/institution/newBorns',
  institutionPregnancyInvestigations: '/institution/investigation-pregnancy',
  institutionPregnancyNewBornInvestigations:
    '/institution/investigation-pregnancy-newborn',
  equipments: {
    root: '/equipments',
    details: (id: string) => `/equipments/${id}/details`
  },
  prescriptions: {
    root: '/prescriptions',
    details: (treatmentPlanId: string, id: string) =>
      `/prescriptions/${treatmentPlanId}/${id}`
  },
  fundingSource: '/funding-source',
  activeSubstances: '/active-substances',
  deviceTypes: '/device-types',
  laboratoryClassifier: {
    root: '/laboratory-classifier',
    valueOptions: '/laboratory-classifier/value-options',
    referenceIntervals: '/laboratory-classifier/reference-intervals',
    testTypes: '/laboratory-classifier/test-types',
    investigationTypes: '/laboratory-classifier/investigation-types'
  },
  treatmentTypesClassifier: {
    root: '/treatment-types-classifier'
  },
  templates: {
    treatments: '/templates/treatments'
  },
  cuatmClassifier: {
    root: '/cuatm-classifier'
  }
}
